/**
 * pipeline-health — "is the machine running?"
 *
 * FEAT-087 / S-379. The handoff reframes Analytics away from vanity metrics:
 * the question this screen answers is whether the pipeline is moving, and
 * where it is stuck. Token totals are a cost report, not an answer to that.
 *
 * Every piece status falls into exactly one of four buckets, so the counts
 * always add up to the total and nothing goes missing into an "other" that
 * hides a problem. An unrecognised status counts as **stopped**, not as
 * moving: a status this build does not know about is not evidence that the
 * work is progressing, and the failure mode to avoid is a dashboard that
 * looks healthy because it silently dropped what it could not classify.
 */

export type HealthBucket = "waiting" | "moving" | "stopped" | "done";

export const HEALTH_LABEL: Record<HealthBucket, string> = {
  waiting: "Waiting on a person",
  moving: "Moving through the pipeline",
  stopped: "Stopped — needs attention",
  done: "Published",
};

export const HEALTH_HINT: Record<HealthBucket, string> = {
  waiting: "The machine has done its part; these are queued for a decision.",
  moving: "An agent has these right now.",
  stopped: "Failed, rejected, or sent back — nothing advances until someone acts.",
  done: "Out the door.",
};

/** Ordered as the screen reads them: what needs you, then what is in flight. */
export const HEALTH_ORDER: readonly HealthBucket[] = [
  "waiting",
  "stopped",
  "moving",
  "done",
];

const BUCKET_OF: Record<string, HealthBucket> = {
  // Waiting on a person
  waiting_for_review: "waiting",
  waiting_for_approval: "waiting",
  approved: "waiting", // approved but not yet scheduled or sent
  // Moving
  proposal: "moving",
  selected: "moving",
  in_progress: "moving",
  ai_publishing: "moving",
  scheduled: "moving",
  // Stopped
  failed: "stopped",
  rejected: "stopped",
  revision_requested: "stopped",
  // Done
  published: "done",
};

export function healthBucketFor(status: string): HealthBucket {
  return BUCKET_OF[status] ?? "stopped";
}

export interface HealthCount {
  bucket: HealthBucket;
  count: number;
  /** The statuses that landed here, for the reader who wants the detail. */
  statuses: string[];
}

export function pipelineHealth(
  rows: readonly { status: string; count: number }[]
): HealthCount[] {
  const counts = new Map<HealthBucket, HealthCount>();
  for (const bucket of HEALTH_ORDER) {
    counts.set(bucket, { bucket, count: 0, statuses: [] });
  }
  for (const row of rows) {
    const entry = counts.get(healthBucketFor(row.status))!;
    entry.count += row.count;
    if (row.count > 0 && !entry.statuses.includes(row.status)) {
      entry.statuses.push(row.status);
    }
  }
  return HEALTH_ORDER.map((b) => counts.get(b)!);
}
